import _ from 'lodash';
import { 
  knowledgeMapDetailInputStates, 
  competencyDocumentItemPrefixes,
  competencyDocumentItemSufixes,
  regexFindDuplicatedEndString,
  MAXIMUM_SHORT_CODE_LENGTH,
  MAXIMUM_TITLE_LENGTH,
} from 'constants/knowledgeMap';
import compareDocumentItems from 'builder/Knowledge/components/TreeView/compareDocumentItems';
import { calculatePrefixNumber } from './generatePrefix';

/**
 * Duplicate the competency item
 * 
 * @param {Object} competencyDocumentItem
 * @param {Object} competencyTree
 * @param {Object} competencyDocument
 * @returns {Array} response of array of objects of duplicated competenct item
 */
export const duplicateCompetencyDocumentItem = (
  competencyDocumentItem, 
  competencyTree, 
  competencyDocument
) => {
  const {  
    COMPETENCY_DOCUMENT_ITEM_PARENT_KEY,
    COMPETENCY_DOCUMENT_ITEM_CHILD_KEY,
  } = competencyDocumentItemPrefixes;
  const { UNDER_SCORE } = competencyDocumentItemSufixes;
  const { TITLEKEY } = knowledgeMapDetailInputStates;
  const duplicatedcompetencyDocumentArray = [];
  const { id: knowledgeMapId } = competencyDocumentItem;
  const competencyDocumentRootItems = competencyTree.getRootItems().sort(compareDocumentItems);
  
  const prefixDecimalNumber = calculatePrefixNumber(
    competencyTree, 
    competencyDocumentItem
  );
  const competencyDocumentItemResult = {...competencyDocumentItem};
  const isParentItem = competencyDocumentRootItems.find((rootItem) => rootItem.id === knowledgeMapId);
  if(isParentItem) {
    competencyDocumentItemResult[TITLEKEY] = `${ COMPETENCY_DOCUMENT_ITEM_PARENT_KEY }${ prefixDecimalNumber }`;
  }
  
  const childItems = competencyTree.getChildren(competencyDocumentItem.id).sort(compareDocumentItems);
  if(childItems && childItems.length > 0) {
    childItems.map( (childItem, index) => {
      const parentKeyWithPrefixDecimal = `${ COMPETENCY_DOCUMENT_ITEM_PARENT_KEY }${ prefixDecimalNumber }${ UNDER_SCORE }`;
      childItem[TITLEKEY] = `${ parentKeyWithPrefixDecimal }${ COMPETENCY_DOCUMENT_ITEM_CHILD_KEY }${ index + 1 }`;
      const duplicatedCompetencyChildItem = duplicateSingleCompetencyDocumentItem(
        childItem, 
        competencyDocument
      );

      duplicatedcompetencyDocumentArray.push(duplicatedCompetencyChildItem);
      competencyDocument.items.push(duplicatedCompetencyChildItem);
    });
  } else if(!isParentItem) {
    const [ parentItemId ] = competencyTree.getAllParentsByDeepChildId(knowledgeMapId);
    const parentItem = competencyTree.itemsById[parentItemId];
    const childKeyWithPrefixDecimal = `${ COMPETENCY_DOCUMENT_ITEM_CHILD_KEY }${prefixDecimalNumber}`;
    competencyDocumentItemResult[TITLEKEY] = `${parentItem[TITLEKEY]}${ UNDER_SCORE }${ childKeyWithPrefixDecimal }`;
  }

  const duplicatedCompetencyParentItem = duplicateSingleCompetencyDocumentItem(
    competencyDocumentItemResult, 
    competencyDocument
  );

  duplicatedcompetencyDocumentArray.push(duplicatedCompetencyParentItem);
  competencyDocument.items.push(duplicatedCompetencyParentItem);

  return duplicatedcompetencyDocumentArray;
};

/**
 * Duplicate the single competency item
 * 
 * @param {Object} competencyDocumentItem
 * @param {Object} competencyDocument
 * @returns {Object} response of object of duplicated competenct item
 */
export const duplicateSingleCompetencyDocumentItem = (competencyDocumentItem, competencyDocument) => {
  const {
    ABBREVIATEDSTATEMENT, 
    HUMANCODINGSCHEME,
  } = knowledgeMapDetailInputStates;

  competencyDocumentItem[ABBREVIATEDSTATEMENT] = buildCompetencyDocumentCopyText(
    competencyDocumentItem[ABBREVIATEDSTATEMENT], 
    competencyDocumentItem,
    competencyDocument
  );
  competencyDocumentItem[HUMANCODINGSCHEME] = buildCompetencyDocumentCopyText(
    competencyDocumentItem[HUMANCODINGSCHEME], 
    competencyDocumentItem,
    competencyDocument,
    true
  );
  return competencyDocumentItem;    
};

/**
 * Build and apppend the prefix copy text for competency item attribute
 * 
 * @param {String} competencyDocumentItemAttribute
 * @param {Object} competencyDocumentItem
 * @param {Object} competencyDocument
 * @param {Boolean} isShortCode
 * @returns {String} response of appended copy text competency item attribute string
 */
export const buildCompetencyDocumentCopyText = (
  competencyDocumentItemAttribute, 
  competencyDocumentItem, 
  competencyDocument, 
  isShortCode,
  isAddCompetencyItem,
) => {
  if(_.isEmpty(competencyDocumentItemAttribute) && isShortCode){
    return competencyDocumentItemAttribute;
  }

  let competencyDocumentAttributeText = competencyDocumentItemAttribute.trim();
  competencyDocumentAttributeText = getActualCompetencyItemAttributeText(competencyDocumentAttributeText);

  const duplicateItems = findDuplicateItems(competencyDocument, isShortCode, competencyDocumentAttributeText);
  
  let competencyDocumentDuplicatedAttribute =  buildCompetencyDocumentItemCopyText(
    competencyDocumentItem, 
    competencyDocumentItemAttribute, 
    isShortCode, 
    duplicateItems, 
    competencyDocumentAttributeText, 
    false,
    isAddCompetencyItem
  );

  const maximumCompetencyItemLength = isShortCode ? MAXIMUM_SHORT_CODE_LENGTH : MAXIMUM_TITLE_LENGTH;
  
  if(competencyDocumentDuplicatedAttribute.trim().length > maximumCompetencyItemLength) {
    competencyDocumentDuplicatedAttribute = trimCompetencyItemAttribute(
      competencyDocumentItem,
      competencyDocumentDuplicatedAttribute.trim(),
      isShortCode,
      duplicateItems,
      maximumCompetencyItemLength
    );
  }

  return competencyDocumentDuplicatedAttribute; 
};

/**
 * Build and apppend the prefix copy text for competency item attribute
 * 
 * @param {Object} competencyDocumentItem
 * @param {String} competencyDocumentItemAttribute
 * @param {Boolean} isShortCode
 * @param {Array} duplicateItems
 * @param {String} competencyDocumentAttributeText
 * @param {Boolean} isTrim
 * @returns {String} response of appended copy text competency item attribute string
 */
export const buildCompetencyDocumentItemCopyText = (
  competencyDocumentItem, 
  competencyDocumentItemAttribute, 
  isShortCode, 
  duplicateItems, 
  competencyDocumentAttributeText, 
  isTrim,
  isAddCompetencyItem,
) => {
  const { TITLEKEY } = knowledgeMapDetailInputStates;
  const { COMPETENCY_DOCUMENT_ITEM_COPY_TEXT } = competencyDocumentItemPrefixes;
  const {OPENBRACKET,
    CLOSEBRACKET,
  } = competencyDocumentItemSufixes;
  
  const titleKeyText = isShortCode ? '' : `${ competencyDocumentItem[TITLEKEY] }-`;
  const competencyDocumentAttributePrefix = isAddCompetencyItem ? `${ titleKeyText }` :
    `${ titleKeyText }${ COMPETENCY_DOCUMENT_ITEM_COPY_TEXT }`;
  let competencyDocumentDuplicatedAttribute = `${ competencyDocumentAttributePrefix } ${ competencyDocumentAttributeText }`;

  if(isAddCompetencyItem && duplicateItems.length >= 1) {
    const competencyDocumentAttributeSufix = `${ OPENBRACKET }${ (duplicateItems.length) }${ CLOSEBRACKET }`;
    competencyDocumentDuplicatedAttribute = `${ competencyDocumentAttributePrefix } ${ competencyDocumentAttributeText }${ competencyDocumentAttributeSufix }`;
  } else if (duplicateItems.length === 1) {
    competencyDocumentDuplicatedAttribute = isTrim 
      ? `${ competencyDocumentAttributePrefix } ${ competencyDocumentAttributeText }` 
      : `${ competencyDocumentAttributePrefix } ${ competencyDocumentItemAttribute }`;
  } else if (duplicateItems.length > 1) {
    const competencyDocumentAttributeSufix = `${ OPENBRACKET }${ (duplicateItems.length - 1) }${ CLOSEBRACKET }`;
    competencyDocumentDuplicatedAttribute = `${ competencyDocumentAttributePrefix } ${ competencyDocumentAttributeText }${ competencyDocumentAttributeSufix }`;
  }
  
  return competencyDocumentDuplicatedAttribute;
};

/**
 * get Actual attribute text
 * 
 * @param {String} competencyDocumentAttributeText
 * @returns {String} - attribute text without 'copy of' and 'ending brackets'
 */
export const getActualCompetencyItemAttributeText = (competencyDocumentAttributeText) => {
  const { COMPETENCY_DOCUMENT_ITEM_COPY_TEXT } = competencyDocumentItemPrefixes;
  const { CLOSEBRACKET } = competencyDocumentItemSufixes;
  if(competencyDocumentAttributeText.startsWith(COMPETENCY_DOCUMENT_ITEM_COPY_TEXT)) {
    competencyDocumentAttributeText = (competencyDocumentAttributeText.replace(COMPETENCY_DOCUMENT_ITEM_COPY_TEXT, '')).trim();
  }

  if(competencyDocumentAttributeText.endsWith(CLOSEBRACKET) ) {
    const startIndex = competencyDocumentAttributeText.match(regexFindDuplicatedEndString);
    competencyDocumentAttributeText = (competencyDocumentAttributeText.substring(0, startIndex.index)).trim();
  }
  return competencyDocumentAttributeText;
};

/**
 * Trims the extra characters while building duplicate items
 * 
 * @param {Object} competencyDocumentItem
 * @param {String} competencyItemAttribute
 * @param {Boolean} isShortCode
 * @param {Array} duplicateItems
 * @param {String} limitValue
 * @returns {String} response of appended copy text to trimmed competency item attribute string
 */
export const trimCompetencyItemAttribute = (
  competencyDocumentItem, 
  competencyItemAttribute, 
  isShortCode, 
  duplicateItems, 
  limitValue
) => {
  const { CLOSEBRACKET } = competencyDocumentItemSufixes;
  const { TITLEKEY } = knowledgeMapDetailInputStates;
  const { COMPETENCY_DOCUMENT_ITEM_COPY_TEXT } = competencyDocumentItemPrefixes;
  let competencyItemPrefixSufixLength = 0;
  const titleKetText = `${ competencyDocumentItem[TITLEKEY] }-`;
  if(competencyItemAttribute.startsWith(titleKetText)) {
    competencyItemAttribute = competencyItemAttribute.replace(titleKetText, '');
  }
  if(competencyItemAttribute.startsWith(COMPETENCY_DOCUMENT_ITEM_COPY_TEXT)) {
    competencyItemPrefixSufixLength = COMPETENCY_DOCUMENT_ITEM_COPY_TEXT.length;
  }
  
  if(competencyItemAttribute.endsWith(CLOSEBRACKET) ) {
    const startIndex = (competencyItemAttribute.match(regexFindDuplicatedEndString)).index;
    const stripIndex = competencyItemAttribute.trim().length - startIndex;
    competencyItemPrefixSufixLength = competencyItemPrefixSufixLength + stripIndex;
  }
  const stripingValue = limitValue - competencyItemPrefixSufixLength;
  let acutualCompetencyItemAttributeText = getActualCompetencyItemAttributeText(competencyItemAttribute.trim());
  acutualCompetencyItemAttributeText = acutualCompetencyItemAttributeText.substring(0, stripingValue);
  acutualCompetencyItemAttributeText = buildCompetencyDocumentItemCopyText(
    competencyDocumentItem, 
    competencyItemAttribute, 
    isShortCode, 
    duplicateItems, 
    acutualCompetencyItemAttributeText, 
    true
  );
  return acutualCompetencyItemAttributeText;
};

/**
 * finds duplicate items in competency Document
 * 
 * @param {Object} competencyDocument
 * @param {Boolean} isShortCode
 * @param {String} competencyDocumentItemAttribute
 * @returns {Array} List of items matching the current item in the competency Document.
 */
export const findDuplicateItems = (competencyDocument, isShortCode, competencyDocumentItemAttribute) => {
  const {
    ABBREVIATEDSTATEMENT, 
    HUMANCODINGSCHEME,
  } = knowledgeMapDetailInputStates;

  const itemAttributeKey = isShortCode ? HUMANCODINGSCHEME : ABBREVIATEDSTATEMENT;

  return competencyDocument.items.filter(
    (item) => item[itemAttributeKey]?.includes(competencyDocumentItemAttribute)
  );
};

